import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { disciplineService } from '../../../services/disciplineService';
import { useNotifications } from '../../../components/notifications/NotificationSystem';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { CSS_CLASSES } from '../../../utils/constants';
import { LoadingSpinner, ConfirmDialog } from '../../../components/shared/UIComponents';
import StandardPage from '../../../components/loading/StandardPage';
import ValuesManagerSkeleton from '../../../components/skeletons/ValuesManagerSkeleton';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { useSchoolFirestore } from '../../../hooks/useSchoolFirestore';
import { useSchool } from '@/contexts/SchoolContext';
import SortableValueTag from './SortableValueTag';
import IconPicker from '../../../components/ui/IconPicker';
import ColorPicker from '../../../components/ui/ColorPicker';
import { getIconByName } from '../../../utils/iconMapping';

export default function ValuesManager({ embedded = false } = {}) {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showValueModal, setShowValueModal] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const { showError, showSuccess, showWarning } = useNotifications();
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const loadRequest = useRef(0);
  const { confirmDialog, showConfirm } = useConfirmDialog();

  // School-scoped CRUD operations
  const { create: createWithSchool, update: updateWithSchool } = useSchoolFirestore();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Form state
  const [valueForm, setValueForm] = useState({
    name: '',
    description: '',
    icon: 'StarIcon',
    color: '#3B82F6'
  });

  const loadValues = useCallback(async () => {
    const request = ++loadRequest.current;
    if (!schoolId) { setValues([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const valuesData = await disciplineService.getValues(schoolId);
      if (request === loadRequest.current) setValues(valuesData);
    } catch (err) {
      if (request !== loadRequest.current) return;
      setError(err.message || 'Failed to load values');
      showError('Failed to load values');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [schoolId, showError]);

  useEffect(() => {
    setValues([]);
    setShowValueModal(false);
    setEditingValue(null);
    loadValues();
    return () => { loadRequest.current += 1; };
  }, [loadValues]);

  // Filter values based on search
  const filteredValues = useMemo(() => {
    if (!searchQuery.trim()) return values;

    const query = searchQuery.toLowerCase();
    return values.filter(value =>
      value.name?.toLowerCase().includes(query) ||
      value.description?.toLowerCase().includes(query)
    );
  }, [values, searchQuery]);

  // Handle value form submission
  const handleValueSubmit = async (e) => {
    e.preventDefault();

    if (!valueForm.name.trim()) {
      showWarning('Value name is required');
      return;
    }

    try {
      // For new values, place at top by finding max sortOrder and adding 1
      let sortOrder = 0;
      if (!editingValue) {
        if (values.length > 0) {
          const maxSortOrder = Math.max(...values.map(v => v.sortOrder ?? 0));
          sortOrder = maxSortOrder + 1;
        }
      }

      const valueData = {
        name: valueForm.name.trim(),
        description: valueForm.description.trim(),
        icon: valueForm.icon,
        color: valueForm.color,
        sortOrder: editingValue ? editingValue.sortOrder : sortOrder, // Preserve order when editing
        active: true,
        createdBy: user?.uid || 'system'
      };

      if (editingValue) {
        await updateWithSchool('values', editingValue.id, valueData);
        showSuccess('Value updated successfully');
      } else {
        const created = await createWithSchool('values', valueData);
        console.log('Created value:', created);
        showSuccess('Value created successfully');
      }

      // Reload values and close modal
      await loadValues();
      closeValueModal();
    } catch (error) {
      console.error('Error saving value:', error);
      showError(error.message || 'Failed to save value');
    }
  };

  // Handle value deletion
  const handleValueDelete = async (value) => {
    const confirmed = await showConfirm({
      title: 'Retire Value?',
      message: `Retire "${value.name}"? Its history will remain.`,
      variant: 'danger',
      confirmText: 'Retire'
    });
    if (!confirmed) {
      return;
    }

    try {
      await disciplineService.deleteValue(value.id, schoolId);
      showSuccess('Value retired successfully');
      await loadValues();
      closeValueModal();
    } catch (error) {
      console.error('Error deleting value:', error);
      showError('Failed to retire value');
    }
  };

  // Modal handlers
  const openValueModal = (value = null) => {
    if (value) {
      setEditingValue(value);
      setValueForm({
        name: value.name || '',
        description: value.description || '',
        icon: value.icon || 'StarIcon',
        color: value.color || '#3B82F6'
      });
    } else {
      setEditingValue(null);
      setValueForm({
        name: '',
        description: '',
        icon: 'StarIcon',
        color: '#3B82F6'
      });
    }
    setShowValueModal(true);
  };

  const closeValueModal = () => {
    setShowValueModal(false);
    setEditingValue(null);
  };

  const clearFilters = () => {
    setSearchQuery('');
  };

  // Handle drag end for reordering values
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredValues.findIndex(value => value.id === active.id);
      const newIndex = filteredValues.findIndex(value => value.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedValues = arrayMove(filteredValues, oldIndex, newIndex);

        // Calculate new sort orders - highest number at top
        const updates = reorderedValues.map((value, index) => ({
          id: value.id,
          sortOrder: reorderedValues.length - index
        }));

        // Optimistically update UI
        setValues(reorderedValues);

        // Update Firebase with new sort orders
        try {
          await disciplineService.updateValueSortOrders(updates, schoolId);
        } catch (error) {
          console.error('Error updating sort order:', error);
          showError('Failed to update value order');
          // Reload values to reset order
          await loadValues();
        }
      }
    }
  };

  const inner = (
    <>
      {/* Value Modal */}
      <AnimatePresence>
        {showValueModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={closeValueModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-base-100 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingValue ? 'Edit Value' : 'Create Value'}
                  </h2>
                  <button
                    onClick={closeValueModal}
                    className="btn btn-ghost btn-circle btn-sm"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleValueSubmit} className="space-y-4">
                  {/* Name (Required) */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">
                        Name <span className="text-error">*</span>
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Respect, Courage, Curiosity"
                      value={valueForm.name}
                      onChange={(e) => setValueForm(prev => ({ ...prev, name: e.target.value }))}
                      className="input input-bordered w-full"
                      required
                      autoFocus
                    />
                  </div>

                  {/* Icon */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Icon</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowIconPicker(true)}
                      className="btn btn-outline justify-start space-x-2 w-full"
                    >
                      {getIconByName(valueForm.icon, "w-5 h-5")}
                      <span>{valueForm.icon.replace('Icon', '')}</span>
                      <PhotoIcon className="w-4 h-4 ml-auto opacity-60" />
                    </button>
                  </div>

                  {/* Color */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Color</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowColorPicker(true)}
                      className="btn btn-outline justify-start space-x-2 w-full"
                    >
                      <div
                        className="w-5 h-5 rounded border border-base-300"
                        style={{ backgroundColor: valueForm.color }}
                      />
                      <span className="font-mono">{valueForm.color}</span>
                    </button>
                  </div>

                  {/* Description (Optional) */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Description</span>
                    </label>
                    <textarea
                      placeholder="Optional description or notes..."
                      value={valueForm.description}
                      onChange={(e) => setValueForm(prev => ({ ...prev, description: e.target.value }))}
                      className="textarea textarea-bordered w-full"
                      rows={3}
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex items-center justify-between pt-6">
                    <div>
                      {editingValue && (
                        <button
                          type="button"
                          onClick={() => handleValueDelete(editingValue)}
                          className="btn btn-error btn-outline"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Retire
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={closeValueModal}
                        className="btn btn-ghost"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                      >
                        {editingValue ? 'Update' : 'Create'} Value
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Icon Picker Modal */}
      {showIconPicker && (
        <IconPicker
          selectedIcon={valueForm.icon}
          onIconSelect={(iconName) => {
            setValueForm(prev => ({ ...prev, icon: iconName }));
            setShowIconPicker(false);
          }}
          onClose={() => setShowIconPicker(false)}
        />
      )}

      {/* Color Picker Modal */}
      {showColorPicker && (
        <ColorPicker
          selectedColor={valueForm.color}
          onColorSelect={(color) => {
            setValueForm(prev => ({ ...prev, color: color }));
            setShowColorPicker(false);
          }}
          onClose={() => setShowColorPicker(false)}
        />
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl mt-8">
        {/* Header */}
        <div className={`${CSS_CLASSES.CARD_ELEVATED} mb-6`}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-base-content mb-2">School values</h1>
              <p className="text-base-content/70">
                Define the values staff can connect to Recognition.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => openValueModal()}
                className={`${CSS_CLASSES.BUTTON_BASE_WITH_SHADOW} btn-primary whitespace-nowrap`}
              >
                <PlusIcon className="w-5 h-5" />
                <span className="hidden sm:inline">Create Value</span>
                <span className="sm:hidden">Value</span>
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-base-content/50" />
              <input
                type="text"
                placeholder="Search values..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input input-bordered w-full pl-10"
              />
              {searchQuery && (
                <button
                  onClick={clearFilters}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/50 hover:text-base-content"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Values List */}
        <div className="card bg-base-100 border border-base-300">
          <div className="p-4 space-y-2 min-h-[200px]">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredValues.map(v => v.id)}
                strategy={verticalListSortingStrategy}
              >
                <AnimatePresence>
                  {filteredValues.map(value => (
                    <motion.div
                      key={value.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <SortableValueTag
                        value={value}
                        onClick={() => openValueModal(value)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Empty State */}
        {filteredValues.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-base-200 mb-4">
              <MagnifyingGlassIcon className="w-8 h-8 text-base-content/50" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {searchQuery ? 'No values found' : 'No values yet'}
            </h3>
            <p className="text-base-content/70">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Create the first value staff can recognise'}
            </p>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
        <ConfirmDialog {...confirmDialog} />
      </>
  );

  if (embedded) {
    if (loading) return <ValuesManagerSkeleton />;
    if (error) return <div className="alert alert-error">{error.message || String(error)}</div>;
    return inner;
  }

  return (
    <StandardPage
      loading={loading}
      error={error}
      skeleton={<ValuesManagerSkeleton />}
    >
      {inner}
    </StandardPage>
  );
}
